import { useEffect, useMemo, useRef, useState } from 'react';
import { applyRevealCursor, hasRevealBacklog, initialRevealCursor, tickReveal, type RevealCursor } from './stream-reveal';
import { groupThreadForDisplay, type ThreadClarification, type ThreadDisplayItem, type ThreadItem, type ThreadNetworkNode } from './thread';
import { resolveToolGroupBodyMode, toolGroupVisibleTools } from './tool-group-peek';
import { HumanGateCard, type GateDecision } from './HumanGateCard';
import { ToolCallCard } from './ToolCallCard';
import { MarkdownPreview } from '../ui/MarkdownPreview';

export interface ClarificationAnswer {
  clarification: ThreadClarification;
  answer: string;
}

function markerClass(tone: string): string {
  if (tone === 'success') return 'text-green-600';
  if (tone === 'error') return 'text-red-600';
  if (tone === 'warning') return 'text-amber-600';
  return 'text-gray-400';
}

export function ChatThread({
  items,
  networkNodes,
  fallbackAgentName,
  busyLabel,
  onAnswer,
  onDecide,
}: {
  items: ThreadItem[];
  networkNodes: ThreadNetworkNode[];
  fallbackAgentName: string;
  busyLabel: string;
  onAnswer: (event: ClarificationAnswer) => void;
  onDecide: (event: GateDecision) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cursors = useRef(new Map<string, RevealCursor>());
  const [revealed, setRevealed] = useState<ThreadItem[]>(items);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedAgents, setExpandedAgents] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let frame = 0;
    const sync = (source: ThreadItem[]) => {
      let needsTick = false;
      const live = new Set<string>();
      const next = source.map((item) => {
        if (item.type !== 'agent') return item;
        live.add(item.id);
        const target = { content: item.content, reasoning: item.reasoning, streaming: item.streaming };
        let cursor = cursors.current.get(item.id);
        if (!cursor) {
          cursor = initialRevealCursor(target);
          if (target.streaming) cursor = tickReveal(cursor, target);
        } else {
          cursor = tickReveal({
            content: Math.min(cursor.content, target.content.length),
            reasoning: Math.min(cursor.reasoning, target.reasoning?.length ?? 0),
          }, target);
        }
        cursors.current.set(item.id, cursor);
        if (hasRevealBacklog(cursor, target)) needsTick = true;
        return applyRevealCursor(item, cursor);
      });
      for (const id of cursors.current.keys()) if (!live.has(id)) cursors.current.delete(id);
      setRevealed(next);
      if (needsTick) frame = requestAnimationFrame(() => sync(source));
    };
    sync(items);
    return () => cancelAnimationFrame(frame);
  }, [items]);

  const display = useMemo(() => groupThreadForDisplay(revealed, networkNodes), [revealed, networkNodes]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 96) el.scrollTop = el.scrollHeight;
  }, [revealed]);

  function renderItem(item: ThreadItem) {
    if (item.type === 'user') {
      return (
        <div>
          <span className="mb-0.5 px-1 text-[10px] uppercase text-gray-400">You{item.queued ? <span className="ml-1 text-amber-600">queued</span> : null}</span>
          <div className="inline-block max-w-[80%] rounded-lg bg-gray-100 px-3 py-2 text-sm dark:bg-gray-800"><MarkdownPreview content={item.content} /></div>
        </div>
      );
    }
    if (item.type === 'agent') {
      return (
        <div>
          <span className="mb-0.5 px-1 text-[10px] uppercase text-gray-400">{item.agentName || fallbackAgentName}{item.streaming ? <span className="ml-1 text-blue-600">streaming...</span> : null}</span>
          {item.reasoning && <div className="mb-1 max-w-[80%] rounded border border-gray-200 px-3 py-2 text-xs italic text-gray-500">{item.reasoning}</div>}
          <div className={`inline-block max-w-[80%] rounded-lg px-3 py-2 text-sm ${item.streaming ? 'streaming-caret' : ''}`} data-streaming={item.streaming ? 'true' : undefined}><MarkdownPreview content={item.content} /></div>
        </div>
      );
    }
    if (item.type === 'tool') return <ToolCallCard item={item} />;
    if (item.type === 'marker') {
      return <div className={`flex items-center gap-2 px-1 text-xs ${markerClass(item.tone)}`} data-testid={`marker-${item.id}`}><span>{item.label}</span></div>;
    }
    if (item.type === 'clarification') {
      return (
        <div className="max-w-[85%] rounded-lg border border-gray-200 p-3" data-testid={`clarification-${item.id}`}>
          <div className="text-sm font-medium">The agent needs your input</div>
          <MarkdownPreview content={item.question} />
          {item.status === 'open' ? (
            <div className="mt-2 flex gap-2">
              <input className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800" placeholder="Type your answer..." value={drafts[item.id] ?? ''} onChange={(event) => setDrafts((current) => ({ ...current, [item.id]: event.target.value }))} onKeyUp={(event) => { if (event.key === 'Enter') submit(item); }} />
              <button type="button" className="rounded bg-blue-600 px-3 text-sm text-white disabled:opacity-50" disabled={!(drafts[item.id] ?? '').trim()} onClick={() => submit(item)}>Answer</button>
            </div>
          ) : item.answer ? <p className="mt-2 text-xs text-gray-500">Answered: "{item.answer}"</p> : null}
        </div>
      );
    }
    return <HumanGateCard gate={item} onDecide={onDecide} />;
  }

  function submit(item: ThreadClarification) {
    const answer = drafts[item.id]?.trim();
    if (!answer) return;
    setDrafts((current) => ({ ...current, [item.id]: '' }));
    onAnswer({ clarification: item, answer });
  }

  function renderLeaf(entry: Extract<ThreadDisplayItem, { kind: 'item' | 'tool-group' }>) {
    if (entry.kind === 'tool-group') {
      const mode = resolveToolGroupBodyMode(entry.pending, expandedGroups[entry.id]);
      const visible = toolGroupVisibleTools(entry.tools, mode);
      const done = entry.tools.filter((tool) => !tool.pending).length;
      return (
        <div data-testid={entry.id}>
          <button type="button" className="flex w-full items-center gap-2 px-1.5 py-1 text-left text-sm" onClick={() => setExpandedGroups((current) => ({ ...current, [entry.id]: !current[entry.id] }))}>
            <span className="font-medium">{entry.pending ? (done > 0 ? `Running tools... ${done}/${entry.tools.length}` : 'Running tools...') : `Ran ${entry.tools.length} tools`}</span>
          </button>
          {visible.map((tool) => <ToolCallCard key={tool.id} item={tool} />)}
        </div>
      );
    }
    return renderItem(entry.item);
  }

  return (
    <div ref={scrollRef} className="flex max-h-[560px] min-h-72 flex-col gap-3 overflow-auto pr-1" data-testid="chat-thread">
      {display.length === 0 && <p className="py-8 text-center text-sm text-gray-400">Attach files, describe the outcome, then send. The agent runs in this session.</p>}
      {display.map((entry) => {
        if (entry.kind === 'subagent') {
          const open = expandedAgents[entry.id] ?? entry.pending;
          return (
            <div key={entry.id} data-testid={entry.id}>
              <button type="button" className="flex w-full items-center gap-2 px-1.5 py-1 text-left text-sm" onClick={() => setExpandedAgents((current) => ({ ...current, [entry.id]: !open }))}>
                <span className="font-medium">{entry.agentName}</span>
                <span className="text-[11px] text-gray-400">{entry.pending ? 'running...' : 'done'}</span>
              </button>
              {open && entry.children.map((leaf) => <div key={leaf.kind === 'tool-group' ? leaf.id : leaf.item.id} className="ml-5 border-l border-gray-200 pl-3">{renderLeaf(leaf)}</div>)}
            </div>
          );
        }
        return <div key={entry.kind === 'tool-group' ? entry.id : entry.item.id}>{renderLeaf(entry)}</div>;
      })}
      {busyLabel && <div className="px-1 text-xs text-gray-400">{busyLabel}</div>}
    </div>
  );
}
